import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { authApi, shopApi } from './lib/api';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { Settings } from './pages/Settings';
import { Products } from './pages/Products';
import { ProductEditor } from './pages/ProductEditor';
import { InvitationActivate } from './pages/InvitationActivate';
import { Orders, OrderDetail } from './pages/Orders';
import { Support, SupportCreate, SupportDetail } from './pages/Support';
import { Reservations, ReservationDetail } from './pages/Reservations';
import type { ShopType } from '@reims-local/types';

interface AuthState {
  loading: boolean;
  user: { id: string; email: string; role: string; shop_id: string | null } | null;
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ loading: true, user: null });

  useEffect(() => {
    authApi
      .me()
      .then((res) => setAuth({ loading: false, user: res.user }))
      .catch(() => setAuth({ loading: false, user: null }));
  }, []);

  if (auth.loading) return <div style={{ padding: 32 }}>Chargement…</div>;

  return (
    <Routes>
      <Route path="/invitation/:token" element={<InvitationActivate onActivated={(u) => setAuth({ loading: false, user: u })} />} />
      {!auth.user || (auth.user.role !== 'merchant' && auth.user.role !== 'admin') ? (
        <>
          <Route path="/login" element={<Login onAuth={(u) => setAuth({ loading: false, user: u })} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      ) : (
        <Route
          path="*"
          element={
            <AppShell
              user={auth.user}
              onLogout={() => setAuth({ loading: false, user: null })}
            />
          }
        />
      )}
    </Routes>
  );
}

function AppShell({
  user,
  onLogout,
}: {
  user: { id: string; email: string; role: string; shop_id: string | null };
  onLogout: () => void;
}) {
  const navigate = useNavigate();
  const [shopName, setShopName] = useState('');
  const [shopType, setShopType] = useState<ShopType | null>(null);

  useEffect(() => {
    if (!user.shop_id) return;
    shopApi
      .me()
      .then((res) => {
        setShopName(res.data.name);
        setShopType(res.data.shop_type);
      })
      .catch(() => undefined);
  }, [user.shop_id]);

  async function handleLogout() {
    try { await authApi.logout(); } catch { /* ignore */ }
    onLogout();
    navigate('/login');
  }

  return (
    <div className="layout">
      <aside className="sidebar">
        <h1>Reims Local</h1>
        <div className="shop-name">{shopName || user.email}</div>
        <nav>
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}>
            Accueil
          </NavLink>
          {shopType !== 'RESTAURANT' && (
            <>
              <NavLink to="/orders" className={({ isActive }) => (isActive ? 'active' : '')}>
                Commandes
              </NavLink>
              <NavLink to="/products" className={({ isActive }) => (isActive ? 'active' : '')}>
                Catalogue
              </NavLink>
            </>
          )}
          {shopType === 'RESTAURANT' && (
            <NavLink to="/reservations" className={({ isActive }) => (isActive ? 'active' : '')}>
              Réservations
            </NavLink>
          )}
          <NavLink to="/support" className={({ isActive }) => (isActive ? 'active' : '')}>
            Support
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => (isActive ? 'active' : '')}>
            Paramètres boutique
          </NavLink>
        </nav>
        <div className="bottom">
          <div style={{ marginBottom: 8 }}>{user.email}</div>
          <button style={{ width: '100%' }} onClick={handleLogout}>Se déconnecter</button>
        </div>
      </aside>
      <main className="main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetailRoute />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/new" element={<ProductEditorRoute />} />
          <Route path="/products/:id" element={<ProductEditorRoute />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/support" element={<Support />} />
          <Route path="/support/new" element={<SupportCreate />} />
          <Route path="/support/:id" element={<SupportDetailRoute />} />
          <Route path="/reservations" element={<Reservations />} />
          <Route path="/reservations/:id" element={<ReservationDetailRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function ProductEditorRoute() {
  const params = useParams<{ id?: string }>();
  return <ProductEditor productId={params.id} />;
}

function OrderDetailRoute() {
  const params = useParams<{ id: string }>();
  if (!params.id) return null;
  return <OrderDetail orderId={params.id} />;
}

function SupportDetailRoute() {
  const params = useParams<{ id: string }>();
  if (!params.id) return null;
  return <SupportDetail ticketId={params.id} />;
}

function ReservationDetailRoute() {
  const params = useParams<{ id: string }>();
  if (!params.id) return null;
  return <ReservationDetail reservationId={params.id} />;
}
